import React, { useEffect, useMemo, useState } from 'react';
import { simboloMoneda } from '../services/negocioActual';
import {
  obtenerPendientes, agregarPendiente, actualizarPendiente, pagarPendiente, despagarPendiente,
  eliminarPendiente, obtenerCierres, cerrarMes, reabrirMes,
} from '../services/cierreService';
import { obtenerCuentas, iconoTipo } from '../services/cuentasService';
import { obtenerMovimientos } from '../services/balanceService';
import { obtenerCategoriasGasto } from '../services/categoriasGastoService';
import CampoNumero from './CampoNumero';
import { mensajeAmigable } from '../utils/errores';
import { aNumero } from '../utils/validacion';
import { useToast } from '../context/ToastContext';
import { fechaLocal } from '../utils/fecha';

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const ymActual = () => fechaLocal().slice(0, 7);
const mesLabel = (ym) => { const [y, m] = ym.split('-').map(Number); return `${MESES[(m || 1) - 1]} ${y}`; };
const desplazarYm = (ym, delta) => {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, (m - 1) + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const mesDe = (iso) => fechaLocal(new Date(iso)).slice(0, 7);
const fmt = (n) => `${simboloMoneda()} ${Number(n || 0).toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Ideas rápidas de pendientes típicos de un negocio.
const SUGERENCIAS = ['Facebook Ads', 'Courier / Envíos', 'Sorteo', 'Proveedor', 'Alquiler', 'Servicios'];

const INGRESO_TIPOS = ['venta', 'ingreso_libre', 'abono'];

export default function CierreView({ onVolver, onCambio }) {
  const { mostrarToast } = useToast();
  const [ym, setYm] = useState(ymActual());
  const [pendientes, setPendientes] = useState(null);
  const [cuentas, setCuentas] = useState([]);
  const [categoriasG, setCategoriasG] = useState([]);
  const [cierres, setCierres] = useState([]);
  const [movsMes, setMovsMes] = useState([]);
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [confirmandoId, setConfirmandoId] = useState(null);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({ concepto: '', monto: '', categoria: '', cuentaId: '', nota: '' });

  const cargar = async (mes = ym) => {
    setError(null);
    try {
      const [pend, cs, cats, cie, movs] = await Promise.all([
        obtenerPendientes(mes),
        obtenerCuentas(),
        obtenerCategoriasGasto(),
        obtenerCierres(),
        obtenerMovimientos({ desde: `${mes}-01` }),
      ]);
      setPendientes(pend);
      setCuentas(cs);
      setCategoriasG(cats);
      setCierres(cie);
      setMovsMes((movs || []).filter((m) => mesDe(m.creado_en) === mes));
      setForm((f) => ({
        ...f,
        cuentaId: f.cuentaId || (cs[0]?.id || ''),
        categoria: f.categoria || (cats[0]?.nombre || 'Otros'),
      }));
    } catch (e) {
      setError(mensajeAmigable(e));
    }
  };

  useEffect(() => {
    cargar(ym);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ym]);

  const sinPagar = (pendientes || []).filter((p) => !p.pagado);
  const totalSinPagar = sinPagar.reduce((s, p) => s + Number(p.monto || 0), 0);
  const totalPagado = (pendientes || []).filter((p) => p.pagado).reduce((s, p) => s + Number(p.monto || 0), 0);

  const cerrado = cierres.find((c) => c.ym === ym);

  const resumen = useMemo(() => {
    const ingresos = movsMes.filter((m) => INGRESO_TIPOS.includes(m.tipo)).reduce((s, m) => s + Number(m.monto || 0), 0);
    const gastos = movsMes.filter((m) => m.tipo === 'gasto').reduce((s, m) => s + Math.abs(Number(m.monto || 0)), 0);
    const saldoCuentas = cuentas.reduce((s, c) => s + Number(c.saldo || 0), 0);
    return { ingresos, gastos, balance: ingresos - gastos, saldoCuentas };
  }, [movsMes, cuentas]);

  const agregar = async (e) => {
    e.preventDefault();
    if (!form.concepto.trim()) { mostrarToast('Escribe qué tienes que pagar', 'error'); return; }
    if (!(aNumero(form.monto) > 0)) { mostrarToast('Escribe el monto (solo números, mayor que 0)', 'error'); return; }
    setGuardando(true);
    try {
      await agregarPendiente({ ...form, ym });
      mostrarToast('Pendiente agregado ✅', 'exito');
      setForm({ concepto: '', monto: '', categoria: form.categoria, cuentaId: form.cuentaId, nota: '' });
      cargar();
    } catch (err) {
      mostrarToast(mensajeAmigable(err), 'error');
    } finally {
      setGuardando(false);
    }
  };

  const toggle = async (p) => {
    try {
      if (p.pagado) await despagarPendiente(p.id);
      else await pagarPendiente(p.id);
      mostrarToast(p.pagado ? 'Marcado como no pagado' : 'Pagado ✓ (registrado como gasto)', 'exito');
      cargar();
      onCambio && onCambio();
    } catch (e) {
      mostrarToast(mensajeAmigable(e), 'error');
    }
  };

  const editar = (p) => {
    if (p.pagado) {
      mostrarToast('Ya está pagado. Desmarcá la casilla ✅ para editarlo, y luego vuelves a marcarlo.', 'error');
      return;
    }
    setEditando(p);
  };

  const guardarEdicion = async (datos) => {
    try {
      await actualizarPendiente(editando.id, datos);
      mostrarToast('Pendiente actualizado ✅', 'exito');
      setEditando(null);
      cargar();
    } catch (e) {
      mostrarToast(mensajeAmigable(e), 'error');
    }
  };

  const borrar = async (id) => {
    try {
      await eliminarPendiente(id);
      mostrarToast('Pendiente eliminado 🗑️', 'exito');
      setConfirmandoId(null);
      cargar();
      onCambio && onCambio();
    } catch (e) {
      mostrarToast(mensajeAmigable(e), 'error');
    }
  };

  const hacerCierre = async () => {
    if (totalSinPagar > 0) {
      const ok = window.confirm(
        `Todavía tienes ${sinPagar.length} pendiente(s) sin pagar (${fmt(totalSinPagar)}). ¿Cerrar el mes de todos modos?`
      );
      if (!ok) return;
    }
    try {
      await cerrarMes(ym, {
        ventas: resumen.ingresos, gastos: resumen.gastos,
        balance: resumen.balance, saldo_cuentas: resumen.saldoCuentas,
      });
      mostrarToast(`${mesLabel(ym)} cerrado ✓`, 'exito');
      cargar();
    } catch (e) {
      mostrarToast(mensajeAmigable(e), 'error');
    }
  };

  const abrir = async () => {
    try {
      await reabrirMes(ym);
      mostrarToast('Mes reabierto', 'exito');
      cargar();
    } catch (e) {
      mostrarToast(mensajeAmigable(e), 'error');
    }
  };

  const nombreCuenta = (id) => cuentas.find((c) => c.id === id)?.nombre || '';
  const input = { width: '100%', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };
  const tarjeta = { backgroundColor: '#fff', borderRadius: '16px', padding: '16px', boxShadow: '0 1px 2px rgba(28,24,48,.04), 0 4px 16px rgba(76,29,149,.05)', border: '1px solid #ECE8F6', marginBottom: '16px' };

  return (
    <div className="k-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
        <h2 className="k-page-title">🧾 Cierre de Mes</h2>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '2px' }}>
            <button onClick={() => setYm(desplazarYm(ym, -1))} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', fontSize: '16px', color: '#6B7280' }}>‹</button>
            <span style={{ minWidth: '120px', textAlign: 'center', fontWeight: 700, fontSize: '13px', color: '#374151' }}>{mesLabel(ym)}</span>
            <button onClick={() => setYm(desplazarYm(ym, 1))} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', fontSize: '16px', color: '#6B7280' }}>›</button>
          </div>
          <button onClick={onVolver} className="k-btn-soft">← Volver</button>
        </div>
      </div>

      {/* Estado del cierre */}
      {cerrado ? (
        <div style={{ ...tarjeta, display: 'flex', alignItems: 'center', gap: '12px', background: '#ECFDF5', border: '1px solid #A7F3D0' }}>
          <span style={{ fontSize: '26px' }}>🔒</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontWeight: 'bold', color: '#065F46' }}>Mes cerrado</p>
            <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#047857' }}>
              Balance del mes {fmt(cerrado.resumen?.balance)} · cerrado el {fechaLocal(new Date(cerrado.cerrado_en))}
            </p>
          </div>
          <button onClick={abrir} style={{ background: 'white', border: '1px solid #6EE7B7', color: '#047857', borderRadius: '8px', padding: '8px 14px', cursor: 'pointer', fontWeight: 700 }}>
            🔓 Reabrir
          </button>
        </div>
      ) : (
        <div style={{ ...tarjeta, display: 'flex', alignItems: 'center', gap: '12px', background: totalSinPagar > 0 ? '#FFFBEB' : '#ECFDF5', border: `1px solid ${totalSinPagar > 0 ? '#FDE68A' : '#A7F3D0'}` }}>
          <span style={{ fontSize: '26px' }}>{totalSinPagar > 0 ? '📋' : '✅'}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            {totalSinPagar > 0 ? (
              <>
                <p style={{ margin: 0, fontWeight: 'bold', color: '#92400E' }}>
                  Te faltan {sinPagar.length} pendiente{sinPagar.length === 1 ? '' : 's'} por pagar
                </p>
                <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#B45309' }}>{fmt(totalSinPagar)} en total antes de cerrar.</p>
              </>
            ) : (
              <>
                <p style={{ margin: 0, fontWeight: 'bold', color: '#065F46' }}>Todo pagado, listo para cerrar ✓</p>
                <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#047857' }}>No tienes pendientes sin pagar este mes.</p>
              </>
            )}
          </div>
        </div>
      )}

      {error && <p style={{ textAlign: 'center', padding: '16px', color: '#991B1B', fontWeight: 600 }}>⚠️ {error}</p>}

      {/* Checklist de pendientes */}
      <div style={tarjeta}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '6px' }}>
          <p style={{ margin: 0, fontWeight: 700, color: '#374151' }}>Pendientes por pagar</p>
          {(totalPagado > 0 || totalSinPagar > 0) && (
            <span style={{ fontSize: '12px', color: '#6B7280' }}>{fmt(totalPagado)} pagado · {fmt(totalSinPagar)} pendiente</span>
          )}
        </div>

        {!pendientes && !error && <p style={{ textAlign: 'center', padding: '16px', color: '#9CA3AF' }}>⏳ Cargando…</p>}
        {pendientes && pendientes.length === 0 && (
          <p style={{ textAlign: 'center', padding: '24px', color: '#9CA3AF' }}>Sin pendientes este mes. Agrega lo que tienes que pagar antes de cerrar (ads, envíos, sorteo…).</p>
        )}
        {pendientes && pendientes.length > 0 && (
          <div>
            {pendientes.map((p) => (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 4px', borderBottom: '1px solid #F3F4F6' }}>
                <button onClick={() => toggle(p)} title={p.pagado ? 'Marcar como no pagado' : 'Marcar como pagado'}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', lineHeight: 1 }}>
                  {p.pagado ? '✅' : '⬜'}
                </button>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontWeight: 600, fontSize: '14px', color: p.pagado ? '#9CA3AF' : '#111827', textDecoration: p.pagado ? 'line-through' : 'none' }}>
                    {p.concepto}
                  </p>
                  <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#9CA3AF' }}>
                    {p.categoria || 'Sin categoría'}{nombreCuenta(p.cuenta_id) ? ` · ${nombreCuenta(p.cuenta_id)}` : ''}{p.pagado ? ' · pagado ✓' : ''}
                  </p>
                  {p.nota && (
                    <p style={{ margin: '3px 0 0', fontSize: '12px', color: '#6B7280', fontStyle: 'italic' }}>📝 {p.nota}</p>
                  )}
                </div>
                <span style={{ fontWeight: 'bold', color: p.pagado ? '#9CA3AF' : '#111827' }}>{fmt(p.monto)}</span>
                {confirmandoId === p.id ? (
                  <>
                    <button onClick={() => borrar(p.id)} style={{ padding: '4px 8px', backgroundColor: '#DC2626', color: 'white', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer' }}>Sí</button>
                    <button onClick={() => setConfirmandoId(null)} style={{ padding: '4px 8px', backgroundColor: '#E5E7EB', color: '#374151', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer' }}>No</button>
                  </>
                ) : (
                  <>
                    <button onClick={() => editar(p)} title="Editar"
                      style={{ padding: '4px 8px', background: 'none', border: '1px solid #D1D5DB', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>✏️</button>
                    <button onClick={() => setConfirmandoId(p.id)} title="Eliminar"
                      style={{ padding: '4px 8px', background: 'none', border: '1px solid #FCA5A5', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>🗑️</button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Sugerencias rápidas */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '14px' }}>
          {SUGERENCIAS.map((s) => (
            <button key={s} type="button" onClick={() => setForm((f) => ({ ...f, concepto: s }))}
              style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '999px', padding: '5px 12px', cursor: 'pointer', fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>
              + {s}
            </button>
          ))}
        </div>

        {/* Formulario nuevo pendiente */}
        <form onSubmit={agregar} style={{ marginTop: '14px', backgroundColor: '#FFF5F7', borderRadius: '12px', padding: '14px', border: '1px solid #FBCFE8' }}>
          <p style={{ margin: '0 0 10px', fontWeight: 'bold', color: 'var(--color-primario,#DB2777)' }}>➕ Nuevo pendiente</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', marginBottom: '10px' }}>
            <div>
              <label style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>¿Qué tienes que pagar?</label>
              <input value={form.concepto} disabled={guardando} onChange={(e) => setForm({ ...form, concepto: e.target.value })} style={input} placeholder="Ej. Facebook Ads de agosto" />
            </div>
            <div>
              <label style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>Monto (L)</label>
              <CampoNumero valor={form.monto} onChange={(v) => setForm({ ...form, monto: v })} deshabilitado={guardando}
                min={0} prefijo="L" placeholder="0.00" estilo={input} />
            </div>
            <div>
              <label style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>Categoría (al pagar)</label>
              <select value={form.categoria} disabled={guardando} onChange={(e) => setForm({ ...form, categoria: e.target.value })} style={input}>
                {categoriasG.map((c) => <option key={c.id} value={c.nombre}>{c.nombre}</option>)}
                {form.categoria && !categoriasG.some((c) => c.nombre === form.categoria) && <option value={form.categoria}>{form.categoria}</option>}
              </select>
            </div>
            <div>
              <label style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>Sale de la cuenta</label>
              <select value={form.cuentaId} disabled={guardando} onChange={(e) => setForm({ ...form, cuentaId: e.target.value })} style={input}>
                {cuentas.length === 0 && <option value="">Sin cuentas — crea una en 🏦 Cuentas</option>}
                {cuentas.map((c) => <option key={c.id} value={c.id}>{iconoTipo(c.tipo)} {c.nombre} — {fmt(c.saldo)}</option>)}
              </select>
            </div>
          </div>
          <div style={{ marginBottom: '10px' }}>
            <label style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>Nota / aclaración <span style={{ color: '#9CA3AF', fontWeight: 400 }}>(opcional)</span></label>
            <textarea value={form.nota} disabled={guardando} rows={2} onChange={(e) => setForm({ ...form, nota: e.target.value })}
              style={{ ...input, resize: 'vertical', fontFamily: 'inherit' }} placeholder="Ej. Corresponde al cierre de agosto + primeros 2 días de septiembre" />
          </div>
          <button type="submit" disabled={guardando}
            style={{ padding: '10px 24px', backgroundColor: guardando ? '#9CA3AF' : 'var(--color-primario,#DB2777)', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: guardando ? 'wait' : 'pointer' }}>
            {guardando ? '⏳ Guardando...' : 'Agregar pendiente'}
          </button>
          <p style={{ margin: '10px 0 0', fontSize: '12px', color: '#9CA3AF' }}>
            Al marcarlo pagado, se registra solo como <b>gasto</b> desde esa cuenta (entra al Balance).
          </p>
        </form>
      </div>

      {/* Resumen del cierre */}
      <div style={tarjeta}>
        <p style={{ margin: '0 0 12px', fontWeight: 700, color: '#374151' }}>🧮 Resumen de {mesLabel(ym)}</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px' }}>
          <ResumenBox etiqueta="Ventas / ingresos" valor={fmt(resumen.ingresos)} color="var(--k-verde)" />
          <ResumenBox etiqueta="Gastos del mes" valor={fmt(resumen.gastos)} color="#DC2626" />
          <ResumenBox etiqueta="Balance del mes" valor={fmt(resumen.balance)} color={resumen.balance >= 0 ? 'var(--k-verde)' : '#DC2626'} />
          <ResumenBox etiqueta="Saldo actual en cuentas" valor={fmt(resumen.saldoCuentas)} color="#374151" />
        </div>
        {totalSinPagar > 0 && (
          <p style={{ margin: '12px 0 0', fontSize: '13px', color: '#B45309' }}>
            ⚠️ Aún faltan {fmt(totalSinPagar)} en pendientes por pagar.
          </p>
        )}
        {!cerrado && (
          <button onClick={hacerCierre}
            style={{ marginTop: '14px', width: '100%', padding: '12px', backgroundColor: 'var(--color-primario,#DB2777)', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>
            🔒 Cerrar {mesLabel(ym)}
          </button>
        )}
      </div>

      {editando && (
        <EditarPendienteModal
          pendiente={editando}
          categorias={categoriasG}
          cuentas={cuentas}
          onCerrar={() => setEditando(null)}
          onGuardar={guardarEdicion}
        />
      )}
    </div>
  );
}

function EditarPendienteModal({ pendiente, categorias, cuentas, onCerrar, onGuardar }) {
  const [concepto, setConcepto] = useState(pendiente.concepto || '');
  const [monto, setMonto] = useState(String(pendiente.monto ?? ''));
  const [categoria, setCategoria] = useState(pendiente.categoria || (categorias[0]?.nombre || 'Otros'));
  const [cuentaId, setCuentaId] = useState(pendiente.cuenta_id || '');
  const [nota, setNota] = useState(pendiente.nota || '');
  const [guardando, setGuardando] = useState(false);
  const input = { width: '100%', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };

  const valido = concepto.trim() && aNumero(monto) > 0;
  const guardar = async () => {
    setGuardando(true);
    await onGuardar({ concepto, monto, categoria, cuentaId, nota });
    setGuardando(false);
  };

  return (
    <div onClick={onCerrar}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: 'white', borderRadius: '14px', padding: '20px', width: '100%', maxWidth: '440px', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <h3 style={{ margin: 0, color: '#374151' }}>✏️ Editar pendiente</h3>
          <button onClick={onCerrar} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#9CA3AF' }}>✕</button>
        </div>
        <div style={{ display: 'grid', gap: '10px' }}>
          <div>
            <label style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>¿Qué tienes que pagar?</label>
            <input value={concepto} onChange={(e) => setConcepto(e.target.value)} style={input} placeholder="Ej. Facebook Ads de agosto" />
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>Monto (L)</label>
            <CampoNumero valor={monto} onChange={setMonto} min={0} prefijo="L" placeholder="0.00" estilo={input} />
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>Categoría (al pagar)</label>
            <select value={categoria} onChange={(e) => setCategoria(e.target.value)} style={input}>
              {categorias.map((c) => <option key={c.id} value={c.nombre}>{c.nombre}</option>)}
              {categoria && !categorias.some((c) => c.nombre === categoria) && <option value={categoria}>{categoria}</option>}
            </select>
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>Sale de la cuenta</label>
            <select value={cuentaId} onChange={(e) => setCuentaId(e.target.value)} style={input}>
              <option value="">— Sin cuenta —</option>
              {cuentas.map((c) => <option key={c.id} value={c.id}>{iconoTipo(c.tipo)} {c.nombre} — {fmt(c.saldo)}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>Nota / aclaración <span style={{ color: '#9CA3AF', fontWeight: 400 }}>(opcional)</span></label>
            <textarea value={nota} rows={2} onChange={(e) => setNota(e.target.value)}
              style={{ ...input, resize: 'vertical', fontFamily: 'inherit' }} placeholder="Ej. Corresponde al cierre de agosto + primeros 2 días de septiembre" />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
          <button onClick={onCerrar} style={{ flex: 1, padding: '10px', background: '#F3F4F6', color: '#374151', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}>Cancelar</button>
          <button onClick={guardar} disabled={!valido || guardando}
            style={{ flex: 2, padding: '10px', background: (!valido || guardando) ? '#9CA3AF' : 'var(--color-primario,#DB2777)', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: (!valido || guardando) ? 'not-allowed' : 'pointer' }}>
            {guardando ? '⏳ Guardando...' : 'Guardar cambios'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ResumenBox({ etiqueta, valor, color }) {
  return (
    <div style={{ background: '#F9FAFB', borderRadius: '10px', padding: '12px', border: '1px solid #F3F4F6' }}>
      <p style={{ margin: '0 0 4px', fontSize: '11px', color: '#6B7280', fontWeight: 600 }}>{etiqueta}</p>
      <p style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', color }}>{valor}</p>
    </div>
  );
}
